import { useSuspenseQuery } from "@tanstack/react-query"
import { GetOrganizatorRoleResult } from "app/auth/queries/getOrganizatorRole"
import { trpc } from "app/trpc"
import type { GetCurrentOrganizationResult } from "app/users/queries/getCurrentOrganization"
import type { GetCurrentUserResult } from "app/users/queries/getCurrentUser"
import { createContext, useContext } from "react"

type UserInfoContextValue = {
  user: GetCurrentUserResult
  // group: GetCurrentGroupResult
  organization: GetCurrentOrganizationResult
  organizatorRole: GetOrganizatorRoleResult
}

const UserInfoContext = createContext<UserInfoContextValue | null>(null)

/**
 * UserInfoProvider fetches all core user data (user, group, organization) once at the app level
 * to prevent duplicate queries from multiple components.
 */
export const UserInfoProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const { data: user } = useSuspenseQuery({
    ...trpc.users.getCurrentUser.queryOptions(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: 10000,
  })

  // This one is separate on purpose. useSuspenseQueries retries all queries 3 times if they fail
  // The result is that only after the 3rd retry, the NoGroupSelectedError is caught and the user is redirected
  // To the group picker page. This way it happens after 1 query
  // const { data: group } = useSuspenseQuery(trpc.users.getCurrentGroup.queryOptions())

  const { data: organization } = useSuspenseQuery({
    ...trpc.users.getCurrentOrganization.queryOptions(),
    staleTime: 300000,
  })

  const { data: organizatorRole } = useSuspenseQuery({
    ...trpc.auth.getOrganizatorRole.queryOptions(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const value = {
    user: user as GetCurrentUserResult,
    // group,
    organization,
    organizatorRole,
  }

  return (
    <UserInfoContext.Provider value={value}>
      {children}
    </UserInfoContext.Provider>
  )
}

/**
 * Hook to get the current user from context (loaded once at app level)
 */
export const useCurrentUserFromContext = () => {
  const context = useContext(UserInfoContext)
  return context?.user ?? null
}

/**
 * Hook to get the current group from context (loaded once at app level)
 */
export const useCurrentGroupFromContext = () => {
  const { data: group } = useSuspenseQuery(
    trpc.users.getCurrentGroup.queryOptions()
  )
  return group ?? null
}

/**
 * Hook to get the current organization from context (loaded once at app level)
 */
export const useCurrentOrganizationFromContext = () => {
  const context = useContext(UserInfoContext)
  return context?.organization ?? null
}

/**
 * Hook to get the organizator role from context (loaded once at app level)
 */
export const useOrganizatorRoleFromContext = () => {
  const context = useContext(UserInfoContext)
  return context?.organizatorRole ?? null
}

/**
 * Hook to get the archive years from context (loaded once at app level)
 */
export const useArchiveYearsFromContext = () => {
  const context = useContext(UserInfoContext)
  return context?.organization?.archiveYears ?? null
}

export const useOptionIsAutoContinueEnabledFromContext = () => {
  const context = useContext(UserInfoContext)
  if (!context?.user) {
    return true
  }
  return context.user.optionIsAutoContinueEnabled
}
